import React from 'react'
import Navbar from '../components/sections/Navbar'
import Footer from '../components/sections/Footer'
import { useSEO } from '../hooks/useSEO'

export default function TermsPage() {
  useSEO({
    title: 'Terms & Conditions | AugmentAppz',
    description: 'Complete terms and conditions governing your use of the AugmentAppz platform and services.',
  })

  return (
    <>
      <Navbar />
      <main className="legal-page">

        <div className="legal-page__hero">
          <div className="container">
            <p className="legal-page__badge">Legal</p>
            <h1 className="legal-page__title">Terms &amp; Conditions</h1>
            <p className="legal-page__subtitle">
              Complete terms and conditions governing your use of the AugmentAppz platform and services.
            </p>
          </div>
        </div>

        <div className="container legal-page__body">

          <p className="legal-page__intro">
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of the website, software, services, and platform
            (collectively, &ldquo;Services&rdquo;) provided by AUGMENTAPPZ PRIVATE LIMITED (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
            By accessing or using our website and Services, you agree to comply with these Terms. If you do not agree
            to these Terms, please do not access or use our Services.
          </p>

          <section className="legal-section">
            <h2 className="legal-section__heading">1. Acceptance of Terms</h2>
            <p className="legal-section__text">
              By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy, as well as
              any other policies or guidelines that may apply. These Terms constitute a legally binding agreement between
              you and AUGMENTAPPZ PRIVATE LIMITED. If you are using the Services on behalf of an organization, you represent
              that you have the authority to bind such organization to these Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">2. Services Provided</h2>
            <h3 className="legal-section__subheading">2.1 Software and Platform Services</h3>
            <p className="legal-section__text">
              AUGMENTAPPZ PRIVATE LIMITED provides software solutions, including custom software development, AI-powered
              applications, platform-as-a-service (PaaS), and software-as-a-service (SaaS) platforms. Our services include
              development, testing, implementation, support, and maintenance.
            </p>
            <h3 className="legal-section__subheading">2.2 SaaS and Subscription Services</h3>
            <p className="legal-section__text">
              We offer a subscription-based model that allows users to access our SaaS platform for developing and deploying
              applications, with flexible and scalable options to meet varying business needs.
            </p>
            <h3 className="legal-section__subheading">2.3 Professional Services and Consulting</h3>
            <p className="legal-section__text">
              In addition to software products, we offer professional consulting, strategic partnerships, and custom solutions
              across various stages of the software lifecycle.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">3. User Responsibilities</h2>
            <h3 className="legal-section__subheading">3.1 Account Registration and Security</h3>
            <p className="legal-section__text">
              You agree to provide accurate, current, and complete information during registration and to keep your account
              information updated. You are responsible for maintaining the confidentiality of your account credentials and
              must notify us immediately of any unauthorized use or breach of security.
            </p>
            <h3 className="legal-section__subheading">3.2 Prohibited Uses</h3>
            <p className="legal-section__text">You agree not to use our Services for any unlawful purpose. Prohibited activities include:</p>
            <ul className="legal-list">
              <li>Engaging in any illegal activity or activity that infringes on the intellectual property rights of others</li>
              <li>Using the Services to distribute malware, viruses, or other harmful computer code</li>
              <li>Attempting to gain unauthorized access to any part of our platform or network</li>
              <li>Interfering with the normal operation of the Services, including disruption of servers or networks</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">4. Intellectual Property Rights</h2>
            <h3 className="legal-section__subheading">4.1 Ownership of Content</h3>
            <p className="legal-section__text">
              All content, materials, software, tools, and intellectual property provided by AUGMENTAPPZ PRIVATE LIMITED are
              owned by the Company or its licensors. You do not acquire any ownership rights to the Company IP by using our
              Services, except for the limited, non-exclusive license granted to you.
            </p>
            <h3 className="legal-section__subheading">4.2 Customer Data</h3>
            <p className="legal-section__text">
              You retain ownership of any data, content, or information you upload to our platform. You grant AUGMENTAPPZ
              PRIVATE LIMITED a worldwide, royalty-free, non-exclusive license to use, process, and store your data solely
              to provide the Services.
            </p>
            <h3 className="legal-section__subheading">4.3 Third-Party Content</h3>
            <p className="legal-section__text">
              Our Services may incorporate third-party software, libraries, or content. These materials are subject to
              separate terms and conditions, and you agree to comply with such terms when using these materials.
            </p>
            <h3 className="legal-section__subheading">4.4 Trademarks and Copyrights</h3>
            <p className="legal-section__text">
              All content on the Platform, including logos, trademarks, and software, is protected by copyright and intellectual
              property rights. You may not use any of our trademarks or proprietary content without express written permission.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">5. Subscription and Payment Terms</h2>
            <h3 className="legal-section__subheading">5.1 Subscription Plans</h3>
            <p className="legal-section__text">
              AUGMENTAPPZ PRIVATE LIMITED offers various subscription plans. Pricing and terms of each plan are available
              on our website. The pricing for our Services may change, and we will notify you in advance.
            </p>
            <h3 className="legal-section__subheading">5.2 Billing and Payment</h3>
            <p className="legal-section__text">
              Payments are due in advance and are non-refundable unless otherwise stated. Subscriptions are prepaid monthly
              or yearly with automatic renewals. We may use third-party payment processors to handle payments.
            </p>
            <h3 className="legal-section__subheading">5.3 Late Payments</h3>
            <p className="legal-section__text">
              If payment is not made on time, access to the subscription may be terminated. Users may reinstate their
              subscription by making payment within 30 days. After 30 days, the subscription will be permanently terminated.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">6. Privacy and Data Protection</h2>
            <p className="legal-section__text">
              By using our Services, you agree to the terms outlined in our Privacy Policy, which describes how we collect,
              use, store, and protect your personal data. AUGMENTAPPZ PRIVATE LIMITED uses SSL encryption for financial
              transactions and implements necessary measures to safeguard personal data.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">7. Termination</h2>
            <h3 className="legal-section__subheading">7.1 Termination by You</h3>
            <p className="legal-section__text">
              You may terminate your account and subscription at any time by notifying us in writing or through the platform
              interface. You will not be entitled to a refund for any fees already paid.
            </p>
            <h3 className="legal-section__subheading">7.2 Termination by Us</h3>
            <p className="legal-section__text">
              We may suspend or terminate your access to our Services without liability if we believe you have violated any
              terms of this Agreement, including illegal activity, infringement of intellectual property rights, or failure
              to comply with payment obligations.
            </p>
            <h3 className="legal-section__subheading">7.3 Effect of Termination</h3>
            <p className="legal-section__text">
              Upon termination, your access to the Services will cease, but you will remain responsible for any outstanding
              fees. Customer Data will be retained for 30 days following the termination date, after which it will be
              permanently deleted or anonymized.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">8. Use of Third-Party Open-Source Libraries</h2>
            <p className="legal-section__text">
              8.1 The Platform utilizes third-party open-source software and libraries. By using the Platform, you acknowledge
              and agree to comply with the licensing terms associated with any third-party software used within the Platform.
            </p>
            <p className="legal-section__text">
              8.2 We may update or change the licensing terms of any third-party software we use. Users agree that they are
              responsible for complying with any applicable licensing requirements for third-party software used in their apps.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">9. Taxes</h2>
            <p className="legal-section__text">
              Users are responsible for any applicable taxes, including VAT, sales tax, or other taxes imposed by relevant
              authorities in connection with their use of the Platform and services. All payments are exclusive of applicable
              taxes unless otherwise stated.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">10. Creation and Hosting of Applications</h2>
            <p className="legal-section__text">
              Users may create and publish applications using the Platform. Users can use their applications for production
              purposes, except when subscribed to the Freemium tier, which may have restrictions. The Company reserves the
              right to limit or modify hosting feature availability based on the subscription tier.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">11. Regulatory Compliance</h2>
            <p className="legal-section__text">
              11.1 You represent, warrant, and agree that you will comply with all applicable laws, regulations, and policies
              related to the development, marketing, sale, and distribution of your application.
            </p>
            <p className="legal-section__text">
              11.2 The Company has no obligation or responsibility for any actions taken by you, including the sale, use, or
              distribution of your app(s). The Client is solely responsible for ensuring that their app(s) comply with all
              applicable laws and regulations.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">12. Marketplace and Application Access</h2>
            <p className="legal-section__text">
              Users can purchase applications from the marketplace. Access to purchased applications is contingent on having
              an active subscription. If the user cancels or terminates the subscription, access to those applications will
              be terminated immediately.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">13. Disclaimers with Generative AI Usage</h2>
            <p className="legal-section__text">
              The Platform may incorporate Generative AI tools to assist in the development of applications. These tools are
              provided &ldquo;as-is&rdquo; and without warranty. We do not guarantee the accuracy, reliability, or suitability of any
              AI-generated content. The responsibility for reviewing and validating such content lies solely with the user.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">14. Warranties; Disclaimer of Warranties</h2>
            <p className="legal-section__text">
              The Platform is provided on an &ldquo;as-is&rdquo; and &ldquo;as-available&rdquo; basis, without warranties of any kind. The Company
              disclaims all warranties, including implied warranties of merchantability, fitness for a particular purpose, or
              non-infringement. The Company does not warrant that the Platform will be error-free or uninterrupted.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">15. Limitation of Liability</h2>
            <p className="legal-section__text">
              To the fullest extent permitted by law, AUGMENTAPPZ PRIVATE LIMITED shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, or data, arising
              out of or in connection with your use of the Services. Users agree to indemnify and hold AUGMENTAPPZ PRIVATE
              LIMITED harmless from any claims, losses, liabilities, or expenses arising out of the services rendered.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">16. Indemnification</h2>
            <p className="legal-section__text">
              You agree to indemnify, defend, and hold harmless AUGMENTAPPZ PRIVATE LIMITED, its officers, directors,
              employees, agents, and affiliates from any and all claims, damages, liabilities, losses, or expenses arising
              from your use of the Services, your violation of these Terms, or your infringement of any intellectual property
              or other rights of any third party.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">17. Force Majeure</h2>
            <p className="legal-section__text">
              AUGMENTAPPZ PRIVATE LIMITED will not be liable for any failure or delay in performance under these Terms if
              caused by events beyond its reasonable control, including natural disasters, acts of war, terrorism, strikes,
              pandemics, government regulations, or internet service failures.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">18. Amendments</h2>
            <p className="legal-section__text">
              We reserve the right to revise, update, and modify these Terms and Conditions at any time. Any revisions will
              be effective immediately upon being posted on the Platform. By continuing to access or use the Platform after
              any such changes, you accept the revised Terms and Conditions.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">19. Governing Law and Dispute Resolution</h2>
            <p className="legal-section__text">
              These Terms are governed by and construed in accordance with the laws of Karnataka. Any disputes arising from
              or related to these Terms will be resolved through binding arbitration in Karnataka, and you agree to submit
              to the jurisdiction of the courts in that location.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">20. Severability</h2>
            <p className="legal-section__text">
              If any provision of these Terms is found to be invalid or unenforceable, such provision shall be construed to
              reflect the original intent of the Parties as closely as possible. The remaining provisions will remain in
              full force and effect.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">21. Notices</h2>
            <p className="legal-section__text">
              All notices and communications shall be in writing, in English, and shall be deemed given if delivered personally,
              by commercial messenger or courier service, mailed by registered or certified mail, or sent via email with
              due acknowledgment.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">22. Access to Website</h2>
            <h3 className="legal-section__subheading">22.1 Website Availability</h3>
            <p className="legal-section__text">
              AUGMENTAPPZ PRIVATE LIMITED strives to ensure the availability of the website at all times but does not
              guarantee uninterrupted or error-free access. The website may be unavailable due to technical issues or
              maintenance.
            </p>
            <h3 className="legal-section__subheading">22.2 Website Use</h3>
            <p className="legal-section__text">
              The use of our website is solely for informational purposes. Access to the website does not include automatic
              access to the platform services unless specifically provided for under these Terms.
            </p>
            <h3 className="legal-section__subheading">22.3 No Platform Access through Website</h3>
            <p className="legal-section__text">
              While the website provides access to information about our services, it does not grant access to the full
              platform services. To use our platform, you must subscribe to the appropriate subscription plan.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section__heading">23. Contact Information</h2>
            <p className="legal-section__text">For any questions or concerns regarding these Terms, please contact us:</p>
            <div className="legal-contact-card">

              {/* Company header */}
              <div className="lcc-company">
                <span className="lcc-company-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V12h6v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="lcc-company-name">AUGMENTAPPZ PRIVATE LIMITED</span>
              </div>

              {/* Info rows */}
              <ul className="lcc-rows" role="list">

                <li className="lcc-row">
                  <span className="lcc-row-icon" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <div className="lcc-row-body">
                    <span className="lcc-row-label">Website</span>
                    <a href="https://augmentappz.ai" target="_blank" rel="noopener noreferrer" className="lcc-row-value">augmentappz.ai</a>
                  </div>
                </li>

                <li className="lcc-row">
                  <span className="lcc-row-icon" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <div className="lcc-row-body">
                    <span className="lcc-row-label">Email</span>
                    <a href="mailto:innovate@augmentappz.ai" className="lcc-row-value">innovate@augmentappz.ai</a>
                  </div>
                </li>

              </ul>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </>
  )
}
